import React from 'react';
import { Alert, Button } from '@patternfly/react-core';
import { SyncAltIcon } from '@patternfly/react-icons';
import { formatDateTimeWithSeconds } from '../../../utils/dateFormat';

// Building blocks shared by every Verda tab: each tab reads live from Verda once on open, then only on
// Refresh (Verda rate-limits at 60 req/min per endpoint), and shows the backend's JSend message as-is.

interface VerdaRefreshButtonProps {
  loading: boolean;
  onRefresh: () => Promise<void>;
  /** What is being refreshed, for the spinner's accessible text (e.g. "locations"). */
  what: string;
}

/** Secondary Refresh button; stays mounted and spins during a refetch, and ignores clicks meanwhile. */
export const VerdaRefreshButton: React.FunctionComponent<VerdaRefreshButtonProps> = ({ loading, onRefresh, what }) => (
  <Button
    variant="secondary"
    icon={<SyncAltIcon />}
    isLoading={loading}
    spinnerAriaValueText={`Refreshing the Verda ${what}`}
    onClick={() => {
      if (!loading) {
        void onRefresh();
      }
    }}
  >
    Refresh
  </Button>
);

/** "Last fetched …" stamp, with seconds so two reads within the same minute stay distinct. */
export const VerdaLastFetched: React.FunctionComponent<{ fetchedAt: string }> = ({ fetchedAt }) => (
  <span className="pf-v6-u-font-size-sm pf-v6-u-text-color-subtle">
    Last fetched {formatDateTimeWithSeconds(fetchedAt)}
  </span>
);

/** The backend's caller-safe JSend error message, rendered verbatim. */
export const VerdaErrorAlert: React.FunctionComponent<{ what: string; message: string }> = ({ what, message }) => (
  <Alert variant="danger" isInline title={`Could not load the Verda ${what}`}>
    {message}
  </Alert>
);
